import React, { useState, useEffect } from 'react';
import { X, Users, Check } from 'lucide-react';
import { contactService } from '../../services/contactService';
import { contactGroupService } from '../../services/contactGroupService';
import toast from 'react-hot-toast';

const AddToGroupModal = ({ isOpen, onClose, contact, onSuccess }) => {
  const [allGroups, setAllGroups] = useState([]);
  const [contactGroups, setContactGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && contact) {
      loadData();
    }
  }, [isOpen, contact]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all groups
      const groups = await contactService.getContactGroups();
      setAllGroups(groups || []);

      // Load groups this contact belongs to
      if (contact.phoneNumber) {
        const response = await contactService.searchContactGroupsByPhoneNumber(contact.phoneNumber, 0, 100);
        const groupsForContact = response.content || [];
        setContactGroups(groupsForContact);
        // Pre-select groups contact already belongs to
        setSelectedGroups(groupsForContact.map(g => g.groupId));
      }
    } catch (error) {
      console.error('Error loading groups:', error);
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleGroup = (groupId) => {
    setSelectedGroups(prev => {
      if (prev.includes(groupId)) {
        return prev.filter(id => id !== groupId);
      } else {
        return [...prev, groupId];
      }
    });
  };

  const handleSave = async () => {
    if (!contact) return;

    // Find groups to add (selected but not in contactGroups)
    const groupsToAdd = selectedGroups.filter(groupId => 
      !contactGroups.some(cg => cg.groupId === groupId)
    );

    // Find groups to remove (in contactGroups but not selected)
    const groupsToRemove = contactGroups
      .filter(cg => !selectedGroups.includes(cg.groupId))
      .map(cg => cg.groupId);

    if (groupsToAdd.length === 0 && groupsToRemove.length === 0) {
      toast.info('No changes to save');
      onClose();
      return;
    }

    setSaving(true);
    try {
      // Add contact to new groups
      for (const groupId of groupsToAdd) {
        await contactGroupService.addContactsToGroup(groupId, [contact.contactId]);
      }

      // Remove contact from deselected groups
      for (const groupId of groupsToRemove) {
        await contactGroupService.removeContactsFromGroup(groupId, [contact.contactId]);
      }

      const totalChanges = groupsToAdd.length + groupsToRemove.length;
      if (groupsToAdd.length > 0 && groupsToRemove.length > 0) {
        toast.success(`Updated: Added to ${groupsToAdd.length} group(s), removed from ${groupsToRemove.length} group(s)`);
      } else if (groupsToAdd.length > 0) {
        toast.success(`Added contact to ${groupsToAdd.length} group(s)`);
      } else {
        toast.success(`Removed contact from ${groupsToRemove.length} group(s)`);
      }

      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating contact groups:', error);
      toast.error(error.response?.data?.message || 'Failed to update contact groups');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          onClick={onClose}
        />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Users className="w-6 h-6 text-primary-600" />
                <h3 className="text-lg font-medium text-gray-900">
                  Add to Groups
                </h3>
              </div>
              <button
                onClick={onClose}
                disabled={saving}
                className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {contact && (
              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm font-medium text-gray-900">{contact.contactName || 'Unnamed'}</p>
                <p className="text-xs text-gray-500">{contact.phoneNumber}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {allGroups.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No groups available</p>
                ) : (
                  <div className="space-y-2">
                    {allGroups.map((group) => {
                      const isSelected = selectedGroups.includes(group.groupId);
                      const isAlreadyMember = contactGroups.some(cg => cg.groupId === group.groupId);

                      return (
                        <div
                          key={group.groupId}
                          onClick={() => handleToggleGroup(group.groupId)}
                          className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                            isSelected
                              ? 'border-primary-500 bg-primary-50'
                              : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{group.groupName}</p>
                            <p className="text-xs text-gray-500">
                              {group.totalContacts} contact{group.totalContacts !== 1 ? 's' : ''}
                              {isAlreadyMember && <span className="ml-2 text-primary-600">(Already a member)</span>}
                            </p>
                          </div>
                          <div className={`flex-shrink-0 w-6 h-6 rounded border-2 flex items-center justify-center ${
                            isSelected
                              ? 'border-primary-600 bg-primary-600'
                              : 'border-gray-300'
                          }`}>
                            {isSelected && <Check className="w-4 h-4 text-white" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium bg-primary-600 text-white hover:bg-primary-700 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={onClose}
              disabled={saving}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddToGroupModal;

